import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUp } from "lucide-react";
import brixigoLogo from "../assets/images/brixigo_logoo.png";
import {
  COMPANY,
  FOOTER_ALL_LINKS,
  FOOTER_COMPANY_LINKS,
  FOOTER_LEGAL_LINKS,
  MARKETING_ROUTES,
} from "./routes";
import "./marketing.css";

function FooterNavCol({ title, links }) {
  return (
    <div className="mk-footer-col">
      <h3 className="mk-footer-head">{title}</h3>
      <nav className="mk-footer-links" aria-label={title}>
        {links.map((link) => (
          <Link key={link.to} to={link.to}>
            {link.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}

export default function Footer() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 420);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="mk-footer">
      <div className="mk-footer-inner">
        <div className="mk-footer-grid">
          <div className="mk-footer-brand">
            <Link to={MARKETING_ROUTES.home} className="mk-footer-logo-link" aria-label="Brixigo home">
              <img src={brixigoLogo} alt="Brixigo" className="mk-footer-logo" />
            </Link>
            <p className="mk-footer-tagline">{COMPANY.description}</p>
            <div className="mk-footer-brand-icons">
              <a href={`mailto:${COMPANY.email}`} aria-label={`Email ${COMPANY.email}`} className="mk-footer-soc">
                <Mail size={16} strokeWidth={2} />
              </a>
              <a href={COMPANY.phoneHref} aria-label={`Call ${COMPANY.phone}`} className="mk-footer-soc">
                <Phone size={16} strokeWidth={2} />
              </a>
              <Link to={MARKETING_ROUTES.contact} aria-label="View company location" className="mk-footer-soc">
                <MapPin size={16} strokeWidth={2} />
              </Link>
            </div>
          </div>

          <FooterNavCol title="Company" links={FOOTER_COMPANY_LINKS} />
          <FooterNavCol title="Legal" links={FOOTER_LEGAL_LINKS} />

          <div className="mk-footer-col mk-footer-touch">
            <h3 className="mk-footer-head">Get in Touch</h3>
            <ul className="mk-footer-contact">
              <li>
                <Mail size={16} strokeWidth={2} aria-hidden="true" />
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
              </li>
              <li>
                <Phone size={16} strokeWidth={2} aria-hidden="true" />
                <a href={COMPANY.phoneHref}>{COMPANY.phone}</a>
              </li>
              <li>
                <MapPin size={16} strokeWidth={2} aria-hidden="true" />
                <span>{COMPANY.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <nav className="mk-footer-sitemap" aria-label="Website pages">
          {FOOTER_ALL_LINKS.map((link, index) => (
            <span key={link.to} className="mk-footer-sitemap-item">
              {index > 0 ? <span className="mk-footer-sep" aria-hidden="true">|</span> : null}
              <Link to={link.to}>{link.label}</Link>
            </span>
          ))}
        </nav>

        <div className="mk-footer-bot">
          <p className="mk-footer-copy">
            © 2026 {COMPANY.legalName}. All rights reserved.
          </p>
        </div>
      </div>

      <button
        type="button"
        className={`mk-back-top${showTop ? " is-visible" : ""}`}
        onClick={scrollTop}
        aria-label="Back to top"
      >
        <ArrowUp size={18} strokeWidth={2.4} />
      </button>
    </footer>
  );
}
